import { useState } from 'react';
import { parsePain001, ParsedPain001 } from '../../../services/xmlParser';
import { parseCsv, ParsedCsv } from '../../../services/csvParser';

export function useFileUpload() {
  const [files, setFiles] = useState<File[]>([]);
  const [parsedJson, setParsedJson] = useState<ParsedPain001 | ParsedCsv | null>(null);
  const [error, setError] = useState<string | null>(null);

  function getFileKey(file: File): string {
    return file.name + '::' + file.size + '::' + file.lastModified;
  }

  function handleFiles(newFiles: File[]): void {
    setFiles(function mergeFiles(prev: File[]): File[] {
      const map = new Map<string, File>();
      prev.concat(newFiles).forEach(function add(file: File): void {
        const key = getFileKey(file);
        if (!map.has(key)) map.set(key, file);
      });
      return Array.from(map.values());
    });
    
    // If any XML files present, read the first one and parse
    const xmlFile = newFiles.find(function isXml(f: File): boolean {
      const name = (f.name || '').toLowerCase();
      const type = (f.type || '').toLowerCase();
      return name.endsWith('.xml') || type.includes('xml');
    });
    
    if (xmlFile) {
      setError(null);
      const reader = new FileReader();
      reader.onload = function onload(): void {
        try {
          const text = String(reader.result || '');
          const json = parsePain001(text);
          setParsedJson(json);
        } catch (e) {
          setParsedJson(null);
          setError(e instanceof Error && e.message ? e.message : 'Failed to parse XML');
        }
      };
      reader.onerror = function onerror(): void {
        setParsedJson(null);
        setError('Failed to read file');
      };
      reader.readAsText(xmlFile);
    } else {
      // Check for CSV files
      const csvFile = newFiles.find(function isCsv(f: File): boolean {
        const name = (f.name || '').toLowerCase();
        const type = (f.type || '').toLowerCase();
        return name.endsWith('.csv') || type.includes('csv') || type === 'text/csv';
      });
      
      if (csvFile) {
        setError(null);
        const reader = new FileReader();
        reader.onload = function onload(): void {
          try {
            const text = String(reader.result || '');
            const parsed = parseCsv(text);
            setParsedJson(parsed);
          } catch (e) {
            setParsedJson(null);
            setError(e instanceof Error && e.message ? e.message : 'Failed to parse CSV');
          }
        };
        reader.onerror = function onerror(): void {
          setParsedJson(null);
          setError('Failed to read file');
        };
        reader.readAsText(csvFile);
      }
    }
  }

  function removeFile(fileToRemove: File): void {
    const removeKey = getFileKey(fileToRemove);
    setFiles(function withoutRemoved(prev: File[]): File[] {
      return prev.filter(function keep(file: File): boolean {
        return getFileKey(file) !== removeKey;
      });
    });
  }

  return {
    files,
    parsedJson,
    error,
    handleFiles,
    removeFile,
    setFiles,
    setParsedJson,
    setError
  };
}

